import React, { useState, useCallback, useRef, forwardRef, useImperativeHandle, useEffect } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCloudUploadAlt } from '@fortawesome/free-solid-svg-icons';

import { useOrganization } from '../../../../context/OrganizationContext';
import { separateDataAttributes } from '../../../elements/DynamicForm/utils/formHelpers';
import FileItem from './FileItem';
import { 
	generateFileHash, 
	generateUniqueKey,
	validateFile,
	AUTO_SCROLL_DELAY,
	DRAG_STATE_FEEDBACK_TIME
} from './helpers';

import styles from './FileDropzone.module.scss';

/**
 * FileDropzone component for drag-and-drop file uploads with real-time progress tracking and validation
 * 
 * Features:
 * - Drag & drop file upload with visual feedback (active, accept, reject states)
 * - Real-time upload progress and status tracking per file
 * - Comprehensive file validation (type, size, duplicates, count limits)
 * - Individual file management with preview, retry, and remove functionality
 * - Automatic form integration with hidden inputs for valid uploads
 * - Smart validation that only marks files as valid after successful upload completion
 * - Accessible design with proper ARIA labels and keyboard navigation
 * - Responsive file preview with horizontal scrolling
 * - Integration with organization context for authentication
 * 
 * Architecture:
 * - FileDropzone handles UI coordination, drag/drop, and file array management
 * - FileItem components handle individual file upload logic and validation state
 * - Validation state is managed by FileItem and only set to true after successful upload
 * - Hidden inputs are created only for fully uploaded and validated files
 * - Invalid states create required hidden inputs to prevent form submission
 *
 * @param {Object} props - Component props
 * @param {string} props.title - Title text displayed in the dropzone area
 * @param {string} props.description - Description text explaining accepted file types and requirements
 * @param {string} props.accept - Accepted file types as MIME types (e.g., "image/*,.pdf") - {@link https://developer.mozilla.org/en-US/docs/Web/HTTP/Basics_of_HTTP/MIME_types/Common_types MDN MIME Types Reference}
 * @param {number} [props.maxFiles=0] - Maximum number of files allowed (0 = unlimited)
 * @param {number} [props.minFiles=0] - Minimum number of files required
 * @param {number} [props.maxFileSize=0] - Maximum file size in MB (0 = unlimited, e.g., 10 for 10MB limit)
 * @param {boolean} [props.required=false] - Whether file upload is required (automatically sets minFiles to 1 if not specified)
 * @param {function} props.onUploadComplete - Callback when all file uploads complete successfully (receives array of identifications extracted from FileObjects)
 * @param {function} props.onChange - Callback when validation state changes (receives form-compatible event object)
 * @param {string} props.name - Input name for form submission (used for hidden input names)
 * @param {string} [props.uploadUrl="/uploads/public"] - API endpoint URL for file uploads
 * @param {string|number|null} [props.refId=null] - Optional reference ID to associate uploads with (e.g., record ID)
 * @param {string|null} [props.refType=null] - Optional reference type to categorize uploads (e.g., "profile", "document")
 * @param {string} [props.variant="light"] - Visual style variant for theming
 * @param {boolean} [props.isValid=true] - External validation state (for form integration)
 * @param {string} props.error - Error message to display below the component
 * @param {string|null} [props.otk=null] - Optional organization token override (falls back to context)
 * 
 * @ref {Object} ref - Imperative handle with methods:
 * @ref {function} ref.reset - Clears all files and resets component state
 * @ref {function} ref.validate - Returns boolean indicating if all files are valid and uploaded
 * 
 */
const FileDropzone = forwardRef((props, ref) => {
	const {
		title,
		description,
		accept,
		maxFiles = 0,
		minFiles: propsMinFiles = 0,
		maxFileSize = 0,
		required = false,
		onUploadComplete,
		onChange,
		name,
		uploadUrl = "/uploads/public?storage_type=tmp",
		refId = null,
		refType = null,
		variant = "light",
		isValid = true,
		error,
		otk: propOtk = null,
		...rest
	} = props;

	// Separate data-* attributes from other props
	const { dataAttributes } = separateDataAttributes(rest);

	

	// Calculate effective min files based on required flag
	const minFiles = required ? (propsMinFiles > 0 ? propsMinFiles : 1) : 0;

	// State for FileObjects array
	const [files, setFiles] = useState([]);
	
	// Drag state for visual feedback
	const [dragState, setDragState] = useState(null); // null, 'active', 'accept', 'reject'

	// Refs
	const fileInputRef = useRef(null);
	const previewScrollerRef = useRef(null);
	const dragStateTimeoutRef = useRef(null);

	// Get organization token from context
	const organization = useOrganization();
	const otk = propOtk || organization?.otk;

	// Auto-reset dragState after timeout for accept/reject states
	useEffect(() => {
		

		// Set new timeout only for accept/reject states
		if (dragState === 'accept' || dragState === 'reject') {
			// Clear existing timeout
			if (dragStateTimeoutRef.current) {
				clearTimeout(dragStateTimeoutRef.current);
				console.log("Cleared existing dragState timeout");
			}

			dragStateTimeoutRef.current = setTimeout(() => {
				setDragState(null);
			}, DRAG_STATE_FEEDBACK_TIME);
			console.log(`Set dragState timeout for ${dragState} state for ${DRAG_STATE_FEEDBACK_TIME}ms`);
		} else {
			if (dragStateTimeoutRef.current) {
				clearTimeout(dragStateTimeoutRef.current);
				console.log("Cleared existing dragState timeout");
			}
		}

		// Cleanup function
		return () => {
			if (dragStateTimeoutRef.current) {
				clearTimeout(dragStateTimeoutRef.current);
				console.log("Cleanup: cleared dragState timeout");
			}
		};
	}, [dragState]);

	// Expose reset method to parent components
	useImperativeHandle(ref, () => ({
		reset: () => {
			setFiles([]);
			if (fileInputRef.current) {
				fileInputRef.current.value = '';
			}
		},
		validate: () => {
			// Special case: if no files are present
			if (files.length === 0) {
				return minFiles === 0; // Valid only if no minimum files required
			}
			
			// All FileItems must have validation: true (now includes upload completion)
			const allValid = files.every(file => file.validation === true);
			if (!allValid) return false;
			
			// Min/Max count check
			if (minFiles > 0 && files.length < minFiles) return false;
			if (maxFiles > 0 && files.length > maxFiles) return false;
			
			return true;
		}
	}), [files, minFiles, maxFiles]);

	// Utility function to check if new file is valid
	const validateNewFile = useCallback((fileObject, files) => {
		const validation = validateFile(fileObject.file, {
			accept,
			allFiles: files.filter(f => f.key !== fileObject.key), // Exclude current file
			maxFiles,
			maxFileSize,
			fileHash: fileObject.fileHash,
		});
		return validation.isValid || false;
	}, [accept, maxFiles, maxFileSize]);

	// Add new file to array with hash and key generation
	const addFile = useCallback(async (newFile) => {
		try {
			const fileHash = await generateFileHash(newFile);

			setFiles(prevFiles => {
				const key = generateUniqueKey(fileHash, prevFiles);

				const fileObject = {
					file: newFile,
					fileHash,
					key,
					validation: false,          // Will be set by FileItem after validation
					identification: null,       // Extracted identification (URL/ID/etc.)
					uploadResult: null         // Complete API response after upload
				};

				const validation = validateNewFile(fileObject, prevFiles);

				if (dragState !== 'reject') {
					setDragState( prev => {
						if (prev !== 'reject') {
							return validation ? 'accept' : 'reject'
						} else {
							return prev; 
						}
					});
				}

				// Auto-scroll to end of list
				setTimeout(() => {
					if (previewScrollerRef.current) {
						previewScrollerRef.current.scrollLeft = previewScrollerRef.current.scrollWidth;
					}
				}, AUTO_SCROLL_DELAY);

				return [...prevFiles, fileObject];
			});
		} catch (error) {
			console.error('Error adding file:', error);
		}
	}, [validateNewFile, dragState]);

	// Remove file from array
	const handleRemoveFile = useCallback((keyToRemove) => {
		setFiles(prevFiles => prevFiles.filter(file => file.key !== keyToRemove));
	}, []);

	// Update validation status of file
	const handleValidationChange = useCallback((key, isValid) => {
		setFiles(prevFiles => 
			prevFiles.map(file => 
				file.key === key 
					? { ...file, validation: isValid }
					: file
			)
		);
	}, []);

	// Handle successful upload completion by updating FileObject
	const handleUploadComplete = useCallback((key, identification, uploadResult) => {
		setFiles(prevFiles => 
			prevFiles.map(file => 
				file.key === key 
					? { ...file, identification, uploadResult }
					: file
			)
		);
	}, []);

	// Handle file input change
	const handleFileInputChange = useCallback((e) => {
		const selectedFiles = Array.from(e.target.files || []);
		selectedFiles.forEach(file => addFile(file));
		
		// Reset input for repeated selection of same file
		e.target.value = '';
	}, [addFile]);

	// Drag & Drop event handlers with visual feedback
	const handleDragOver = useCallback((e) => {
		e.preventDefault();
		e.stopPropagation();

		setDragState('active'); // Fallback to active state

	}, []);

	const handleDragEnter = useCallback((e) => {
		e.preventDefault();
		e.stopPropagation();

		setDragState('active');
	}, []);

	const handleDragLeave = useCallback((e) => {
		e.preventDefault();
		e.stopPropagation();
		
		// Only reset state if leaving the dropzone entirely
		if (!e.currentTarget.contains(e.relatedTarget)) {
			setDragState(null);
		}
	}, []);

	const handleDrop = useCallback((e) => {
		e.preventDefault();
		e.stopPropagation();
		
		const droppedFiles = Array.from(e.dataTransfer.files || []);
		
		// Process files regardless of validation (visual feedback only)
		if (droppedFiles.length > 0) {
			droppedFiles.forEach(file => addFile(file));
		} else {
			setDragState(null);
		}
	}, [addFile]);

	// Calculate validation status - now only needs to check validation property
	const isInputValid = useCallback(() => {
		// Special case: if no files are required and none are present
		if (files.length === 0) {
			return minFiles === 0; // Valid only if no minimum files required
		}
		
		// Check if all files have passed validation (now includes upload completion)
		const hasValidFiles = files.every(file => file.validation === true);
		
		// Check min/max file count requirements
		const hasEnoughFiles = files.length >= minFiles;
		const hasNotTooManyFiles = maxFiles === 0 || files.length <= maxFiles;
		
		// All conditions must be met: validation passed and count requirements met
		return hasValidFiles && hasEnoughFiles && hasNotTooManyFiles;
	}, [files, maxFiles, minFiles]);

	// Effect to notify parent component of changes
	useEffect(() => {
		// Extract identifications from successfully uploaded files
		const uploadedIdentifications = files
			.filter(file => file.validation === true && file.identification)
			.map(file => file.identification);

		if (onChange) {
			const inputValid = isInputValid();
			const event = {
				target: {
					name,
					value: inputValid ? uploadedIdentifications : "",
					type: "file",
					validity: {
						valid: inputValid
					},
				},
			};
			onChange(event);
		}
		
		// Check if all files are uploaded and notify completion
		if (onUploadComplete && files.length > 0 && uploadedIdentifications.length === files.length) {
			onUploadComplete(uploadedIdentifications);
		}
	}, [files, onChange, onUploadComplete, name, isInputValid]);

	return (
		<div 
			className={`${styles.fileDropzone} ${styles[`fileDropzone--${variant}`]} ${!isValid ? styles.invalid : ""} ${
				dragState === 'active' ? styles.activeDrop :
				dragState === 'accept' ? styles.acceptDrop :
				dragState === 'reject' ? styles.rejectDrop : ''
			}`}
			onDragOver={handleDragOver}
			onDragEnter={handleDragEnter}
			onDragLeave={handleDragLeave}
			onDrop={handleDrop}
		>
			{/* Drag & Drop Area */}
			<div
				className={styles.dropArea}
				aria-label="File upload dropzone"
			>
				{/* Hidden File Input */}


			<input
				type="file"
				ref={fileInputRef}
				className={styles.fileInput}
				onChange={handleFileInputChange}
				accept={accept}
				multiple={maxFiles !== 1}
				tabIndex="-1"
			/>
			{/* Dropzone Content */}
				<div className={styles.dropzoneContent}>
					<FontAwesomeIcon
						icon={faCloudUploadAlt}
						className={styles.uploadIcon}
						aria-hidden="true"
					/>
					<h3 className={styles.title}>{title}</h3>
					<p className={styles.description}>
						{description}
					</p>
					
					{/* Dynamic file size info */}
					{maxFileSize > 0 && (
						<div className={styles.fileSizeInfo}>
							Maximale Dateigröße: {maxFileSize} MB pro Datei
						</div>
					)}
					
					{/* Dynamic file count info */}
					{((required && minFiles > 0) || maxFiles > 0) && (
						<div className={styles.fileCountInfo}>
							{(() => {
								const currentCount = files.length;
								const effectiveMinFiles = minFiles;
								const hasMin = effectiveMinFiles > 0;
								const hasMax = maxFiles > 0;
								
								// Determine status and message
								let message = '';
								let isValid = true;
								
								if (hasMin && hasMax) {
									// Both min and max set
									if (currentCount === 0) {
										message = `Bitte ${effectiveMinFiles} bis ${maxFiles} Dateien angeben.`;
										isValid = false;
									} else if (currentCount < effectiveMinFiles) {
										message = `Bitte mindestens ${effectiveMinFiles} Dateien angeben. Aktuell: ${currentCount}`;
										isValid = false;
									} else if (currentCount > maxFiles) {
										message = `Bitte maximal ${maxFiles} Dateien angeben! Aktuell: ${currentCount}`;
										isValid = false;
									} else {
										message = `${currentCount} von ${effectiveMinFiles} bis ${maxFiles} Dateien angegeben.`;
										isValid = true;
									}
								} else if (hasMin) {
									// Only min set
									if (currentCount === 0) {
										message = `Bitte mindestens ${effectiveMinFiles} ${effectiveMinFiles === 1 ? 'Datei' : 'Dateien'} angeben.`;
										isValid = false;
									} else if (currentCount < effectiveMinFiles) {
										message = `Bitte mindestens ${effectiveMinFiles} ${effectiveMinFiles === 1 ? 'Datei' : 'Dateien'} angeben! Aktuell: ${currentCount}`;
										isValid = false;
									} else {
										message = `${currentCount} ${currentCount === 1 ? 'Datei' : 'Dateien'} angegeben.`;
										isValid = true;
									}
								} else if (hasMax) {
									// Only max set
									if (currentCount === 0) {
										message = `Bitte maximal ${maxFiles} ${maxFiles === 1 ? 'Datei' : 'Dateien'} angeben.`;
										isValid = true;
									} else if (currentCount > maxFiles) {
										message = `Bitte maximal ${maxFiles} ${maxFiles === 1 ? 'Datei' : 'Dateien'} angeben! Aktuell: ${currentCount}`;
										isValid = false;
									} else {
										message = `${currentCount} von maximal ${maxFiles} ${maxFiles === 1 ? 'Datei' : 'Dateien'} angegeben.`;
										isValid = true;
									}
								}
								
								// Highlight the current count when it's out of range
								if (!isValid && currentCount > 0 && message.includes(`Aktuell: ${currentCount}`)) {
									const parts = message.split(`Aktuell: ${currentCount}`);
									return (
										<span className={styles.fileCountInvalid}>
											{parts[0]}Aktuell: <strong>{currentCount}</strong>{parts[1]}
										</span>
									);
								}
								
								return (
									<span className={isValid ? styles.fileCountValid : styles.fileCountInvalid}>
										{message}
									</span>
								);
							})()}
						</div>
					)}
					
					<button
						type="button"
						className={styles.browseButton}
						onClick={() => fileInputRef.current?.click()}
					>
						{maxFiles === 1 ? 'Datei auswählen' : 'Dateien auswählen'}
					</button>
				</div>
			</div>

			{/* File Preview Container */}
			{files.length > 0 && (
				<div className={styles.previewContainer}>
					<div className={styles.previewScroller} ref={previewScrollerRef}>
						{files.map((fileObject) => (
							<FileItem
								key={fileObject.key}
								fileObject={fileObject}
								allFiles={files}
								maxFiles={maxFiles}
								maxFileSize={maxFileSize}
								accept={accept}
								uploadUrl={uploadUrl}
								refId={refId}
								refType={refType}
								otk={otk}
								onRemove={handleRemoveFile}
								onValidationChange={handleValidationChange}
								onUploadComplete={handleUploadComplete}
							/>
						))}
					</div>
				</div>
			)}

			

			{/* Hidden Inputs for Form Submission */}
			
				<input
					{...dataAttributes}
					key={`${name}-files`}
					type="hidden"
					name={name}
					required={!isInputValid()}
					data-field={name}
					value={
						files
							.filter(file => file.validation === true && file.identification)
							.length > 0
							? JSON.stringify({
								medias: files
									.filter(file => file.validation === true && file.identification)
									.map(file => file.identification)
							})
							: ""
					}
				/>
			

			{/* Error Display */}
			{error && <div className={styles.errorMessage}>{error}</div>}
		</div>
	);
});

FileDropzone.displayName = "FileDropzone";

export default FileDropzone;